import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  BlockLang,
  ButtonBlock,
  CardGroupBlock,
  HeadingBlock,
  IconBadgeBlock,
  ListBlock,
  MessageBlock,
  MessageDocument,
  NoteBlock,
  ParagraphBlock,
  StepperBlock,
} from 'src/app/core/models/message-blocks';

/**
 * Renders a block-based MessageDocument (core/models/message-blocks.ts) into
 * themed UI. Thin orchestrator: iterates blocks, skips hidden ones, and delegates
 * each block to its own sub-component. Styling lives in the sub-components + theme
 * tokens; the DB only carries content + structure. Reusable anywhere a
 * configurable message is shown.
 */
@Component({
  selector: 'app-message-renderer',
  templateUrl: './message-renderer.component.html',
  styleUrls: ['./message-renderer.component.scss'],
})
export class MessageRendererComponent {
  @Input() doc?: MessageDocument | null;

  /** Optional language override; falls back to the active translate language. */
  @Input() lang?: BlockLang;

  /** Emits a button block's `action` string when clicked. */
  @Output() action = new EventEmitter<string>();

  constructor(private translate: TranslateService) {}

  get effectiveLang(): BlockLang {
    if (this.lang) return this.lang;
    return this.translate.currentLang?.startsWith('pt') ? 'pt' : 'en';
  }

  // ngSwitchCase does not narrow the union under strictTemplates, so cast in
  // these typed helpers (the runtime type is guaranteed by the matched case).
  asIconBadge = (b: MessageBlock): IconBadgeBlock => b as IconBadgeBlock;
  asHeading = (b: MessageBlock): HeadingBlock => b as HeadingBlock;
  asParagraph = (b: MessageBlock): ParagraphBlock => b as ParagraphBlock;
  asCardGroup = (b: MessageBlock): CardGroupBlock => b as CardGroupBlock;
  asNote = (b: MessageBlock): NoteBlock => b as NoteBlock;
  asButton = (b: MessageBlock): ButtonBlock => b as ButtonBlock;
  asStepper = (b: MessageBlock): StepperBlock => b as StepperBlock;
  asList = (b: MessageBlock): ListBlock => b as ListBlock;
}
